// SYSTEM_ROUTE: /knowledge/notes
import { useState } from 'react';
import type { Block, BlockType, Note } from '@life-os/app-core';
import { BLOCK_TYPES, makeBlock, noteTodoProgress, noteWikiLinks } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-sm)',
  padding: '6px 10px',
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

const BLOCK_PREFIX: Record<BlockType, string> = {
  paragraph: '',
  heading: '# ',
  todo: '',
  bullet: '• ',
  quote: '“ ',
  code: '</>',
};

function NoteEditor({ note, onChange }: { note: Note; onChange: () => void }) {
  const services = useServices();
  const [blocks, setBlocks] = useState<Block[]>([...note.blocks]);
  const [title, setTitle] = useState(note.title);
  const [tagText, setTagText] = useState(note.tags.join(', '));

  const persist = async (nextBlocks: Block[], nextTitle = title, nextTags = tagText) => {
    setBlocks(nextBlocks);
    await services.notes.update(note.id, {
      title: nextTitle.trim() || 'Untitled',
      blocks: nextBlocks,
      tags: nextTags.split(',').map((t) => t.trim()).filter(Boolean),
    });
    onChange();
  };

  const updateBlock = (id: string, patch: Partial<Block>) => {
    setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const commitBlocks = () => void persist(blocks);

  const addBlock = (type: BlockType) => {
    void persist([...blocks, makeBlock(type)]);
  };

  const removeBlock = (id: string) => {
    void persist(blocks.filter((b) => b.id !== id));
  };

  const progress = noteTodoProgress({ blocks });
  const links = noteWikiLinks({ blocks });

  return (
    <div className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commitBlocks}
        aria-label="Note title"
        style={{ ...inputStyle, fontSize: '1.05rem', fontWeight: 600, borderRadius: 'var(--lc-radius-full)' }}
      />

      <Stack>
        {blocks.map((b) => (
          <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            {b.type === 'todo' ? (
              <input
                type="checkbox"
                checked={b.checked}
                onChange={(e) => {
                  const next = blocks.map((x) => (x.id === b.id ? { ...x, checked: e.target.checked } : x));
                  void persist(next);
                }}
                aria-label="Toggle to-do"
                style={{ marginTop: 8 }}
              />
            ) : (
              <span style={{ width: 28, textAlign: 'right', color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)', marginTop: 6 }}>
                {BLOCK_PREFIX[b.type]}
              </span>
            )}
            <textarea
              value={b.text}
              onChange={(e) => updateBlock(b.id, { text: e.target.value })}
              onBlur={commitBlocks}
              placeholder={b.type === 'code' ? 'code…' : b.type === 'heading' ? 'Heading' : 'Write… use [[Title]] to link'}
              aria-label={`${b.type} block`}
              style={{
                ...inputStyle,
                flex: 1,
                minHeight: b.type === 'code' ? 56 : 32,
                resize: 'vertical',
                fontFamily: b.type === 'code' ? 'var(--lc-font-mono, monospace)' : 'inherit',
                fontWeight: b.type === 'heading' ? 600 : 400,
                textDecoration: b.type === 'todo' && b.checked ? 'line-through' : 'none',
                opacity: b.type === 'todo' && b.checked ? 0.6 : 1,
              }}
            />
            <Button variant="ghost" onClick={() => removeBlock(b.id)} aria-label="Remove block">
              ×
            </Button>
          </div>
        ))}
      </Stack>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {BLOCK_TYPES.map((t) => (
          <Button key={t} variant="ghost" onClick={() => addBlock(t)}>
            + {t}
          </Button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>Tags</span>
        <input
          value={tagText}
          onChange={(e) => setTagText(e.target.value)}
          onBlur={commitBlocks}
          placeholder="comma, separated"
          aria-label="Tags"
          style={{ ...inputStyle, flex: '1 1 160px', borderRadius: 'var(--lc-radius-full)' }}
        />
        {progress.total > 0 ? (
          <Chip tone="neutral">
            {progress.done}/{progress.total} done
          </Chip>
        ) : null}
        {links.length > 0 ? <Chip tone="primary">{links.length} link{links.length === 1 ? '' : 's'}</Chip> : null}
        <Button variant="ghost" onClick={() => void services.notes.archive(note.id).then(onChange)}>
          Archive
        </Button>
      </div>
    </div>
  );
}

export function NotesScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [query, setQuery] = useState('');

  const notes = useAsync<readonly Note[]>(() => services.notes.list(), [revision]);

  const create = async () => {
    if (title.trim().length === 0) return;
    await services.notes.create({ title, blocks: [makeBlock('paragraph')] });
    setTitle('');
    refresh();
  };

  const all = notes.value ?? [];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? all.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.tags.some((t) => t.toLowerCase().includes(q)) ||
          n.blocks.some((b) => b.text.toLowerCase().includes(q)),
      )
    : all;

  return (
    <>
      <ScreenHeader title={<>Notes</>} subtitle={<>Block documents with tags and [[wiki links]]. Everything is preserved and linkable — link two
            notes by name and they connect in the graph.</>} />

      <Pane title="New note">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: 1 }}
            value={title}
            placeholder="Note title…"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
            aria-label="New note title"
          />
          <Button variant="primary" onClick={() => void create()}>
            Create
          </Button>
        </div>
      </Pane>

      <Pane title="Library" hint={`${all.length} note${all.length === 1 ? '' : 's'}`}>
        <Stack>
          <input
            className="lc-cmdbar"
            value={query}
            placeholder="Filter by title, tag, or text…"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter notes"
          />
          {filtered.length === 0 ? (
            <EmptyState title={all.length === 0 ? 'No notes yet' : 'No matches'}>
              {all.length === 0
                ? 'Create your first note above. Use blocks for structure and [[links]] to connect ideas.'
                : 'Try a different search.'}
            </EmptyState>
          ) : (
            filtered.map((n) => <NoteEditor key={String(n.id)} note={n} onChange={refresh} />)
          )}
        </Stack>
      </Pane>
    </>
  );
}
